import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, ArrowRight, Key, CreditCard, Sparkles, Monitor, Crown } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { cn } from '../lib/utils';
import sixthLogo from '../assets/icon.svg';

const SubscriptionSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(15);

  // type parameter: 'byok', 'sixthcredits', or 'subscription' (fixed)
  const type = searchParams.get('type') || 'subscription';

  // Type-specific details
  const typeDetails = {
    subscription: {
      title: 'Subscription Activated!',
      description: 'Your subscription is now active. Enjoy full access to all features and premium AI models.',
      icon: Crown,
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      borderColor: 'border-primary/20',
      glowColor: 'bg-primary/20',
      features: [
        'Full platform access',
        'All latest AI models',
        'Priority support',
        'Unlimited agent mode'
      ],
      ctaText: 'Go to Dashboard',
      ctaAction: () => navigate('/vscode/dashboard'),
      secondaryText: 'Continue with VSCode',
      secondaryAction: () => {
        window.location.href = 'vscode://sixth.sixth-ai/?command=payment-success';
      }
    },
    byok: {
      title: 'BYOK Subscription Activated!',
      description: 'Your Bring Your Own Key subscription is now active. You can use your own API keys with full platform access.',
      icon: Key,
      color: 'text-purple-400',
      bgColor: 'bg-purple-500/10',
      borderColor: 'border-purple-500/20',
      glowColor: 'bg-purple-500/20',
      features: [
        'Full platform access',
        'Use your own API keys',
        'All AI models supported',
        'Priority support'
      ],
      ctaText: 'Continue with VSCode',
      ctaAction: () => {
        window.location.href = 'vscode://sixth.sixth-ai/?command=payment-success';
      },
      secondaryText: 'Go to Dashboard',
      secondaryAction: () => navigate('/vscode/dashboard')
    },
    sixthcredits: {
      title: 'Credits Added Successfully!',
      description: 'Your Sixth Credits have been added to your account. Start using AI assistance right away.',
      icon: CreditCard,
      color: 'text-cyan-400',
      bgColor: 'bg-cyan-500/10',
      borderColor: 'border-cyan-500/20',
      glowColor: 'bg-cyan-500/20',
      features: [
        'Credits added to your balance',
        'No API keys needed',
        'Instant access to all models',
        'Pay only for what you use'
      ],
      ctaText: 'Go to Dashboard',
      ctaAction: () => navigate('/vscode/dashboard'),
      secondaryText: null,
      secondaryAction: null
    }
  };

  const currentType = typeDetails[type] || typeDetails.subscription;
  const TypeIcon = currentType.icon;

  // Auto-redirect countdown - always redirect to unified dashboard
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate('/vscode/dashboard');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate]);

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-foreground font-sans overflow-hidden">
      {/* Background Effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        <motion.div
          animate={{ x: [0, 30, -20, 0], y: [0, -40, -60, 0], scale: [1, 1.05, 1.1, 1] }}
          transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
          className={cn("absolute -top-20 -left-20 w-[500px] h-[500px] rounded-full blur-[120px]", currentType.glowColor)}
        />
        <motion.div
          animate={{ x: [0, -30, 20, 0], y: [0, 40, 60, 0], scale: [1, 1.1, 0.95, 1] }}
          transition={{ duration: 25, repeat: Infinity, ease: "easeInOut", delay: 2 }}
          className="absolute top-1/2 -right-20 w-[400px] h-[400px] rounded-full bg-green-500/20 blur-[100px]"
        />
      </div>

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 border-b border-white/5 bg-[#0a0a0f]/80 backdrop-blur-md">
        <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6 lg:px-8">
          <div className="flex items-center gap-2 text-xl font-bold bg-gradient-to-br from-[#E005FF] to-[#A73CFF] bg-clip-text text-transparent">
            <img src={sixthLogo} alt="Sixth AI" className="h-8 w-8" />
            Sixth AI
          </div>
        </nav>
      </header>

      {/* Main Content */}
      <main className="relative z-10 min-h-screen flex items-center justify-center px-6 pt-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-lg"
        >
          <Card className={cn("border-green-500/20 bg-[#121219]/90 backdrop-blur-xl shadow-2xl shadow-green-500/10")}>
            <CardHeader className="text-center pb-4">
              {/* Success Icon */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
                className="mx-auto mb-6"
              >
                <div className="relative">
                  <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center border border-green-500/30">
                    <CheckCircle className="w-10 h-10 text-green-500" />
                  </div>
                  <motion.div
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="absolute inset-0 rounded-full bg-green-500/20 -z-10"
                  />
                </div>
              </motion.div>

              <Badge className="mb-4 bg-green-500/10 text-green-400 border-green-500/20 mx-auto">
                <Sparkles className="w-3 h-3 mr-1" /> Payment Successful
              </Badge>

              <CardTitle className="text-2xl font-bold text-white">
                {currentType.title}
              </CardTitle>
              <CardDescription className="text-muted-foreground">
                {currentType.description}
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-6">
              {/* Type Badge */}
              <div className={cn(
                "p-4 rounded-xl border flex items-center gap-4",
                currentType.bgColor,
                currentType.borderColor
              )}>
                <div className={cn("p-3 rounded-lg", currentType.bgColor)}>
                  <TypeIcon className={cn("w-6 h-6", currentType.color)} />
                </div>
                <div>
                  <div className="text-white font-semibold">
                    {type === 'byok' ? 'BYOK Subscription' : type === 'sixthcredits' ? 'Sixth Credits' : 'Subscription Plan'}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {type === 'byok' ? 'Bring Your Own Key' : type === 'sixthcredits' ? 'Pay-As-You-Go Credits' : 'Fixed Subscription'}
                  </div>
                </div>
              </div>

              {/* Features */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-white">What's included:</h4>
                <ul className="space-y-2">
                  {currentType.features.map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3 pt-2">
                <Button
                  onClick={currentType.ctaAction}
                  className="w-full text-white border-0 bg-gradient-to-r from-[#E005FF] to-[#A73CFF] hover:opacity-90"
                >
                  {type === 'byok' && <Monitor className="w-4 h-4 mr-2" />}
                  {currentType.ctaText}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>

                {currentType.secondaryText && (
                  <Button
                    variant="outline"
                    onClick={currentType.secondaryAction}
                    className="w-full"
                  >
                    {currentType.secondaryText}
                  </Button>
                )}

                <p className="text-center text-xs text-muted-foreground">
                  Auto-redirecting to dashboard in {countdown} seconds...
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Support Note */}
          <p className="text-center text-sm text-muted-foreground mt-6">
            Need help? Contact us at{' '}
            <a href="mailto:ope@trysixth.com" className="text-primary hover:underline">
              ope@trysixth.com
            </a>
          </p>
        </motion.div>
      </main>
    </div>
  );
};

export default SubscriptionSuccess;
